import { listUnits } from "@/features/geography/api";
import { explain } from "@/shared/api/problem";
import type { MobilizerDetailResponse } from "@/shared/api/types";
import { AsyncMultiSelect } from "@/shared/ui/filters";
import { Modal } from "@/shared/ui/Modal";
import { ErrorStrip, Note } from "@/shared/ui/states";
import { useEffect, useRef, useState } from "react";
import { createMobilizer, listMobilizers, updateMobilizer } from "./api";
import { ROLES, ROLE_ORDER, roleLabel } from "./vocab";
import { DatePicker } from '@/components/erp/date-picker'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, countryOf, phoneIsValid, DEFAULT_COUNTRY } from '@/components/erp/phone-input'

/**
 * Create and edit, in one dialog.
 *
 * ## The phone field is write-only
 *
 * On create it is required and goes straight into the encrypted column. On edit
 * it starts EMPTY, and an empty phone means "leave it alone" — because the
 * server will not return the current number to pre-fill it with, and it should
 * not. A form that silently posts a masked string back as the new number is the
 * failure mode this avoids.
 *
 * ## Role and unit have to agree, and the form says so before the server does
 *
 * The rule is one bound: a role may sit at its own level or below it, never
 * above. The hint updates as the role changes, so somebody choosing "Ward
 * captain" and then a county sees why it will be refused instead of reading a
 * 400.
 *
 * ## No permission matrix here
 *
 * PollSphere's form drawer carried an eighteen-row per-mobilizer permission
 * grid. Axis does not: permissions belong to the `MOBILIZER` *role* in RBAC,
 * which is edited on the Roles screen and applies to everyone who holds it. Two
 * places to grant the same thing is how a permission ends up granted in one and
 * revoked in the other.
 */
export function MobilizerFormDialog({
  open,
  existing,
  onClose,
  onSaved,
}: {
  open: boolean;
  existing?: MobilizerDetailResponse | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const editing = Boolean(existing);
  const p = existing?.profile;

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [nationalId, setNationalId] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("MOBILIZER");
  const [joinedOn, setJoinedOn] = useState("");
  const [notes, setNotes] = useState("");
  const [unit, setUnit] = useState<{ value: string; label: string }[]>([]);
  const [supervisor, setSupervisor] = useState<
    { value: string; label: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the dialog OPENS, and when it opens on a different person.
  //
  // The dependency is `p?.id`, not `existing` — `existing` is `detail.data`, a
  // fresh object on every parent `reload()`, so depending on it would wipe
  // whatever the user had typed the moment any background refetch landed. The
  // seed values are read through a ref for the same reason.
  const seed = useRef({ existing, p });
  seed.current = { existing, p };
  useEffect(() => {
    if (!open) return;
    const { existing, p } = seed.current;
    setError(null);
    setFullName(p?.fullName ?? "");
    setPhone("");
    setNationalId("");
    setEmail(p?.email ?? "");
    setRole(p?.roleCode ?? "MOBILIZER");
    setJoinedOn(existing?.joinedOn ?? "");
    setNotes(existing?.notes ?? "");
    setUnit(
      p?.primaryUnitId
        ? [{ value: p.primaryUnitId, label: p.primaryUnitName ?? "Home unit" }]
        : [],
    );
    setSupervisor(
      p?.supervisorId
        ? [{ value: p.supervisorId, label: p.supervisorName ?? "Supervisor" }]
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, p?.id]);

  const shallowest =
    ROLES[role as keyof typeof ROLES]?.shallowestUnit ?? "WARD";
  const myLevel = ROLES[role as keyof typeof ROLES]?.level ?? 99;

  const valid =
    fullName.trim().length > 1 &&
    (editing || phoneIsValid(phone)) &&
    (editing || unit.length === 1);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editing && p) {
        const saved = await updateMobilizer(
          p.id,
          {
            fullName: fullName.trim(),
            // Omitted entirely when blank — an empty string would be a request to
            // erase the number, which is not what "I did not touch this" means.
            ...(phone
              ? { phone, phoneCountry: countryOf(phone, phoneCountry) }
              : {}),
            ...(nationalId.trim() ? { nationalId: nationalId.trim() } : {}),
            email: email.trim() || null,
            role,
            joinedOn: joinedOn || null,
            notes: notes.trim() || null,
          },
          p.version,
        );
        onSaved(saved.profile.id);
      } else {
        const created = await createMobilizer({
          fullName: fullName.trim(),
          phone,
          phoneCountry: countryOf(phone, phoneCountry),
          nationalId: nationalId.trim() || null,
          email: email.trim() || null,
          role,
          primaryUnitId: unit[0]?.value,
          additionalUnitIds: [],
          supervisorId: supervisor[0]?.value ?? null,
          joinedOn: joinedOn || null,
          notes: notes.trim() || null,
        });
        onSaved(created.profile.id);
      }
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={editing ? `Edit ${p?.fullName ?? "mobilizer"}` : "Add a mobilizer"}
      description={
        editing
          ? "Changing the home unit or the reporting line is done from the Units & team tab, where the consequences are shown."
          : "A mobilizer is a person. Giving them a login is a separate step, and most never need one."
      }
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="erp-btn-primary"
            disabled={busy || !valid}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Add mobilizer"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Full name" required>
            <input
              className="erp-input w-full"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
            />
          </Field>

          <Field
            label={editing ? "New phone number" : "Phone number"}
            required={!editing}
            hint={
              editing
                ? "Leave blank to keep the current number. It is encrypted, so it cannot be shown here to edit."
                : "Stored encrypted and blind-indexed, and masked everywhere it is displayed."
            }
          >
            <PhoneInput
              value={phone}
              onChange={setPhone}
              country={phoneCountry}
              onCountryChange={setPhoneCountry}
            />
          </Field>

          <Field
            label="National ID"
            hint="Optional, and blind-indexed when given — it is what catches the same person entered twice."
          >
            <input
              className="erp-input w-full font-mono"
              value={nationalId}
              autoComplete="off"
              onChange={(e) => setNationalId(e.target.value)}
              placeholder={editing ? "Unchanged" : "12345678"}
            />
          </Field>

          <Field
            label="Email"
            hint="Not unique — two ward captains sharing a family address is real."
          >
            <input
              className="erp-input w-full font-mono"
              value={email}
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.co.ke"
            />
          </Field>

          <Field
            label="Role"
            required
            hint={`May be assigned at ${shallowest.replace(/_/g, " ").toLowerCase()} level or below, never above.`}
          >
            <select
              className="erp-input w-full"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLE_ORDER.map((c) => (
                <option key={c} value={c}>
                  {ROLES[c].label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Joined on">
            <DatePicker value={joinedOn} onChange={setJoinedOn} className="w-full" />
          </Field>
        </div>

        {!editing && (
          <>
            <Field
              label="Home unit"
              required
              hint="Every scope query uses this one. Extra units are added afterwards, from the Units & team tab."
            >
              <AsyncMultiSelect
                label="Home unit"
                selected={unit}
                onChange={(next) => setUnit(next.slice(-1))}
                placeholder="Search counties, constituencies, wards, stations"
                search={async (term) => {
                  const found = await listUnits({ q: term } as never, 0, 10);
                  return found.content.map((u) => ({
                    value: u.id,
                    label: u.name,
                    sub: `${u.unitType.replace(/_/g, " ").toLowerCase()} · ${u.code}`,
                  }));
                }}
              />
            </Field>

            <Field
              label="Reports to"
              hint="Optional. Somebody with no supervisor is a root of the tree, which is what a national director is."
            >
              <AsyncMultiSelect
                label="Supervisor"
                selected={supervisor}
                onChange={(next) => setSupervisor(next.slice(-1))}
                placeholder="Search by name"
                search={async (term) => {
                  const page = await listMobilizers(
                    { q: term, status: "ACTIVE" },
                    undefined,
                    25,
                  );
                  return (
                    page.content
                      // Strictly by rank, same as the server: a peer cannot supervise a peer.
                      .filter((m) => (ROLES[m.roleCode]?.level ?? 99) < myLevel)
                      .map((m) => ({
                        value: m.id,
                        label: m.fullName,
                        sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? "no unit"}`,
                      }))
                  );
                }}
              />
            </Field>
          </>
        )}

        <Field label="Notes">
          <textarea
            className="erp-input w-full min-h-20"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Strengths, languages spoken, anything the next coordinator should know"
          />
        </Field>

        {!editing && (
          <Note tone="info">
            No permissions are set here. What a mobilizer may do comes from the{" "}
            <strong>MOBILIZER</strong> role in Roles &amp; Hierarchy, and
            applies to everyone who holds it — one place to grant, one place to
            revoke.
          </Note>
        )}
      </div>
    </Modal>
  );
}

/** Wraps the control so the label is bound to it — an unbound label is not a label. */
function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block min-w-0">
      <span className="block text-erp-xs text-muted-foreground mb-1">
        {label}
        {required && <span className="text-red-600 dark:text-red-400"> *</span>}
      </span>
      {children}
      {hint && (
        <span className="block text-erp-xs text-muted-foreground/80 mt-1">
          {hint}
        </span>
      )}
    </label>
  );
}
